import { useEffect, useId } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { ChevronDown, ExternalLink, MessagesSquare, ShoppingBag } from 'lucide-react';
import { TUOLEN_BRAND } from '@cartiq/shared/brand';
import { useDismissibleDisclosure } from '../../hooks/useDismissibleDisclosure';
import { APP_SWITCHER_LINKS } from '../../lib/appSwitcher';
import { cn } from '../../lib/utils';

export function AppSwitcher() {
  const { open, setOpen, rootRef, triggerRef, close, onBlur, onKeyDown } = useDismissibleDisclosure();
  const id = useId();
  const panelId = `cartiq-app-switcher-${id}`;
  const descriptionId = `${panelId}-description`;
  const location = useLocation();

  // The header survives route changes, including the browser's Back button.
  useEffect(() => { setOpen(false); }, [location.key, setOpen]);

  return (
    <div ref={rootRef} className="relative shrink-0" onBlur={onBlur} onKeyDown={onKeyDown}>
      <button
        ref={triggerRef}
        type="button"
        aria-label="Switch apps, current app Cartiq"
        aria-expanded={open}
        aria-controls={panelId}
        title="Switch apps — Cartiq by Tuolen"
        onClick={() => setOpen((value) => !value)}
        className={cn(
          'flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border border-border text-primary transition-colors hover:bg-accent-light focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 lg:w-auto lg:gap-2 lg:px-2.5',
          open && 'bg-accent-light',
        )}
      >
        <img src={TUOLEN_BRAND.assets.mark} alt="" aria-hidden="true" width={28} height={28} className="h-7 w-7 object-contain" />
        <span aria-hidden="true" className="hidden text-left lg:block">
          <span className="block text-sm font-semibold leading-tight">{TUOLEN_BRAND.name}</span>
          <span className="block text-[10px] leading-tight text-mid">{TUOLEN_BRAND.parentName} apps</span>
        </span>
        <ChevronDown aria-hidden="true" className={cn('hidden h-3.5 w-3.5 text-mid lg:block', open && 'rotate-180')} />
      </button>

      <nav
        id={panelId}
        hidden={!open}
        aria-label="Tuolen apps"
        aria-describedby={descriptionId}
        className="fixed inset-x-4 top-[5.5rem] max-h-[calc(100dvh-6.5rem)] overflow-y-auto rounded-xl border border-border bg-white shadow-float animate-pop-in sm:absolute sm:inset-x-auto sm:left-0 sm:top-full sm:mt-2 sm:w-80 sm:max-w-[calc(100vw-2rem)]"
      >
        <p className="border-b border-border px-4 py-3 text-sm font-semibold text-primary">Tuolen apps</p>
        <ul className="space-y-1 p-1.5">
          {APP_SWITCHER_LINKS.map((app) => {
            const Icon = app.current ? ShoppingBag : MessagesSquare;
            const className = cn(
              'flex min-h-16 items-center gap-3 rounded-lg px-3 py-2.5 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-accent',
              app.current ? 'bg-accent-light text-accent' : 'text-primary hover:bg-surface',
            );
            const contents = (
              <>
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-accent-light text-accent">
                  <Icon aria-hidden="true" className="h-5 w-5" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold">{app.name}</span>
                  <span className="mt-0.5 block text-xs text-mid">{app.description}</span>
                </span>
                {app.current
                  ? <span className="shrink-0 text-[10px] font-semibold">Current app</span>
                  : <ExternalLink aria-hidden="true" className="h-4 w-4 shrink-0 text-mid" />}
              </>
            );

            return (
              <li key={app.id}>
                {app.external ? (
                  <a
                    href={app.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    referrerPolicy="no-referrer"
                    onClick={() => close(true)}
                    onAuxClick={() => close(true)}
                    className={className}
                  >
                    {contents}
                  </a>
                ) : (
                  <Link to={app.href} aria-current="true" onClick={() => close(true)} className={className}>
                    {contents}
                  </Link>
                )}
              </li>
            );
          })}
        </ul>
        <p id={descriptionId} className="border-t border-border px-4 py-3 text-xs leading-relaxed text-mid">
          Each app keeps its own sign-in and data.
        </p>
      </nav>
    </div>
  );
}
